import { createSelector } from 'reselect';

import { namespace, PermissionsStoreSlice } from './store';
import { selectConfig } from '@common-modules/engines/selectors';
import {
  selectApplicationConfigs,
  selectIsApplicationConfigsLoaded,
  selectRawApplicationConfigs,
  appConfigValue,
} from '@common/state/modules/app';
import { merge } from 'lodash';
import {
  DEFAULT_TRASH_RETENTION_DAYS,
  DEFAULT_THUMBNAIL_REQUEST_TIMEOUT,
  DEFAULT_THUMBNAIL_RUNNING_QUEUE_TIMEOUT,
} from '@helpers/constants';
import {
  editorPermissions,
  RolePermissions,
  viewOnlyPermissions,
  reviewModePermissions,
  statusLockChangePermissions,
} from './rolePermissions';
import { validateTrue } from '@helpers/utils';
import { MediaDetailsState } from '@common/state/modules/mediaDetails/selectors';
import { IN_REDACTION_TAG_KEY } from '@common-modules/mediaDetails/actions/actions';
import {
  selectContentTypeHasImage,
  selectTags,
} from '@common-modules/mediaDetails/selectors/general';
import {
  getRedactionStatusIdentity,
  isRedactionStatusLockEdits,
  resolveRedactionStatusKey,
  REDACTION_STATUS_CORRECTION_NEEDED,
} from '@common-modules/mediaDetails/redactionStatuses';
import {
  selectHasLoadedRedactionStatuses,
  selectRedactionCustomStatuses,
  selectRedactionStatusSettings,
} from '@redact-modules/redactionStatuses/selectors';

export const selectPermissions = (state: PermissionsStoreSlice) =>
  state[namespace];

export const selectAllowDownload = createSelector(
  selectPermissions,
  (s) => s.allowDownload
);

export const selectAllowUpload = createSelector(
  selectPermissions,
  (s) => s.allowUpload
);

export const selectAllowRunDetection = createSelector(
  selectPermissions,
  (s) => s.allowRunDetection
);

export const selectAllowRunRedaction = createSelector(
  selectPermissions,
  (s) => s.allowRunRedaction
);

export const selectAllowRunTranscription = createSelector(
  selectPermissions,
  (s) => s.allowRunTranscription
);

// Resolve a single Boolean app-config straight from the RAW config records.
// We can't use `selectApplicationConfigs` for this: it drops any config that
// resolves to a falsy value (`if (!value) return acc`), so a Boolean set to
// `false` — exactly the "disabled" signal we need — vanishes from that map.
// Reading the raw record and running the shared `appConfigValue` resolver
// (which combines `value || defaultValue` and compares to "true") preserves
// the false. Returns undefined when the org has no such config.
const booleanAppConfig = (
  rawConfigs: ReturnType<typeof selectRawApplicationConfigs>,
  key: string
): boolean | undefined => {
  const record = rawConfigs?.find((c) => c.configKey === key);
  return record ? appConfigValue(record) === true : undefined;
};

// A default-ENABLED flag (the redaction modes video / audio / image; also
// reused for folderViewTiles, enablePageDeletion and enableTextOverlay). A
// dedicated Boolean app-config (`enableVideoRedaction` etc.) is authoritative
// when present — including when it's `false`. Otherwise fall back to the
// merged featureFlags, and finally to enabled: a missing/failed config must
// not silently block existing customers (the opposite of
// `enableDocumentRedaction`, the newer opt-in feature).
const redactionModeEnabled = (
  appConfigBoolean: boolean | undefined,
  flagValue: unknown
): boolean => {
  if (appConfigBoolean !== undefined) {
    return appConfigBoolean;
  }
  return flagValue !== false && flagValue !== 'false';
};

export const selectFeatureFlags = createSelector(
  selectConfig,
  selectApplicationConfigs,
  selectIsApplicationConfigsLoaded,
  selectRawApplicationConfigs,
  (config, appConfigs, isApplicationConfigsLoaded, rawConfigs) => {
    let featureFlags = config?.featureFlags || {};

    // sometimes the values of features flags are string "true" instead of correct type (VE-17384)
    // TODO find cleaner/more compact way to do this
    const featureFlagsFixed: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(featureFlags)) {
      // values that are expected to be a number
      if (
        [
          // eslint-disable-next-line @cspell/spellchecker
          'relockSleep',
          'thumbnailRequestTimeout',
          'thumbnailRunningQueueTimeout',
        ].includes(key)
      ) {
        featureFlagsFixed[key] = Number(value);
        continue;
      }

      // the rest of the flags are boolean
      if (value !== undefined) {
        featureFlagsFixed[key] = validateTrue(value);
      }
    }

    featureFlags = {
      ...featureFlags,
      ...featureFlagsFixed,
    };

    const appConfigFeatureFlagsJSON = (appConfigs.featureFlags || {}) as Record<
      string,
      boolean | number
    >;

    // merge json together (appConfig will replace any keys also in featureFlags)
    featureFlags = merge({}, featureFlags, appConfigFeatureFlagsJSON);

    // check for boolean fields - set true is boolean is true or in json feature flags  (to enable backwards compat for orgs that were enabled using JSON feature flags)
    featureFlags = {
      ...featureFlags,
      voiceMask: !!appConfigs.voiceMask || featureFlags.voiceMask,
      inverseBlur: !!appConfigs.inverseBlur || featureFlags.inverseBlur,
      folderView: !!appConfigs.folderView || featureFlags.folderView,
      folderViewUser:
        !!appConfigs.folderViewUser || featureFlags.folderViewUser,
      // VE-25124: the folder-view thumbnail tile mode defaults to ENABLED —
      // same default-true semantics as the redaction-mode flags: an explicit
      // appConfig `false` (or featureFlags false) turns it off, a missing
      // config leaves it on. The per-user flag stays opt-in (it only matters
      // as an override when the org flag is explicitly off).
      folderViewTiles: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'folderViewTiles'),
        featureFlags.folderViewTiles
      ),
      folderViewTilesUser:
        !!appConfigs.folderViewTilesUser || featureFlags.folderViewTilesUser,
      disableUpload: !!appConfigs.disableUpload || featureFlags.disableUpload,
      // VE-23391: kill-switch — when true, PDFs ingest through the PDF converter
      // engine instead of the fast direct-asset path.
      disableFastPdfIngestion:
        !!appConfigs.disableFastPdfIngestion ||
        featureFlags.disableFastPdfIngestion,
      // VE-30513: kill-switch — when true, Redact does NOT attach an
      // `original-media` asset for video, audio or image uploads itself,
      // and keeps `url` in the engine payload so the GLC ingester's own
      // writer runs. Default off so the app-side attach is live by default;
      // flip on per-org to roll back without a Redact deploy. Flipping it on
      // also puts the 12 h signed upload URL back on the queryable task
      // payload for that org — the exposure VE-30513 removed.
      //
      // DEPRECATE once the app-side attach has proven itself in production.
      // It exists to de-risk the rollout, and its "on" state is strictly
      // worse than its "off" state (no app-side asset AND the signed URL
      // back on the task payload), so it should not outlive the migration.
      disableOriginalMediaAttach:
        !!appConfigs.disableOriginalMediaAttach ||
        featureFlags.disableOriginalMediaAttach,
      useGPU: !!appConfigs.useGPU || featureFlags.useGPU,
      enableDocumentRedaction:
        !!appConfigs.enableDocumentRedaction ||
        featureFlags.enableDocumentRedaction,
      // On-screen text overlay tool: default ENABLED — same semantics as the
      // redaction modes rather than the opt-in `enableDocumentRedaction`. It
      // gates the tool button, its "T" keyboard shortcut, and the shortcut
      // listing; an org that doesn't want the tool turns it off explicitly by
      // setting the `enableTextOverlay` app-config (or feature flag) to false.
      enableTextOverlay: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'enableTextOverlay'),
        featureFlags.enableTextOverlay
      ),
      // Watermarks (VE-24693): default ENABLED, like the redaction modes and
      // `enablePageDeletion` rather than the opt-in `enableDocumentRedaction`.
      // The ticket asks for no flag at all; this one exists as a kill switch,
      // because the feature depends on a `watermarkRegistryId` being configured
      // and the library falls back to per-device localStorage when it is not —
      // an org that hits that needs a way off the feature that is not a
      // redeploy. Default ON so it is not shipped dark: an org that does not
      // want it sets the `enableWatermarks` app-config (or flag) to false.
      enableWatermarks: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'enableWatermarks'),
        featureFlags.enableWatermarks
      ),
      // Bates stamping (VE-24447): default ENABLED, like the redaction modes
      // and `enableWatermarks` rather than the opt-in
      // `enableDocumentRedaction`. The flag exists as a kill switch, not as an
      // opt-in: shipping it dark would hide the feature from every org until
      // each one configured it. An org that does not want it sets the
      // `enableBatesStamping` app-config (or feature flag) to false — that is
      // also the rollback, with no deploy, and documents already exported are
      // unaffected.
      enableBatesStamping: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'enableBatesStamping'),
        featureFlags.enableBatesStamping
      ),
      // First-playback auto-engage of the cluster panel's "On screen only"
      // filter (VE-25876): default ENABLED, like the redaction modes rather
      // than the opt-in `enableDocumentRedaction` — the behaviour already
      // shipped, so an absent config must leave it as operators found it.
      //
      // It gates the AUTOMATIC engage only, not the toggle: an org that turns
      // this off still has the filter, operators just have to reach for it
      // themselves. That is the point of the switch — the auto-engage is a
      // guess about what a reviewer wants, and an org whose reviewers scan the
      // full cluster list while footage runs needs a way to stop it guessing
      // without losing the filter or waiting for a Redact deploy.
      autoEngageOnScreenFilter: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'autoEngageOnScreenFilter'),
        featureFlags.autoEngageOnScreenFilter
      ),
      // Delete Page (drop a page from the burned-in PDF entirely): default
      // ENABLED, so it behaves like the redaction modes rather than like the
      // opt-in `enableDocumentRedaction`. Distinct from Remove Page, which
      // keeps the page and blanks it — Delete changes the output page count.
      // An org that doesn't want that turns it off explicitly by setting the
      // `enablePageDeletion` app-config (or feature flag) to false.
      enablePageDeletion: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'enablePageDeletion'),
        featureFlags.enablePageDeletion
      ),
      // Redact Page (VE-25621): opt-in (default DISABLED, like
      // enableDocumentRedaction). Gates the tool button, its click mode and
      // the page / thumbnail context-menu items. An org turns it on by setting
      // the `enableRedactPage` app-config (or feature flag) to true. Turning it
      // off again leaves boxes already added as ordinary manual redactions.
      enableRedactPage:
        !!appConfigs.enableRedactPage || featureFlags.enableRedactPage,
      // Speaker separation: opt-in (default DISABLED, like
      // enableDocumentRedaction). Gates every path that can START an SS job —
      // the upload DAG, the file picker's checkbox, the Run Engines modal's
      // checkbox, and the ▾ menu item on the transcript tab. There is no
      // button: it was removed because it fired the engine on click where its
      // neighbour opened a confirmation, so one spot behaved two ways.
      //
      // Off by default because the engine-side transcript-ready path is still
      // being stabilized, so an org enables it explicitly once ready.
      // Displaying results is deliberately NOT gated: a TDO that already has
      // speaker segments still renders them, so turning the flag off stops new
      // jobs without hiding work already done.
      enableSpeakerSeparation:
        !!appConfigs.enableSpeakerSeparation ||
        featureFlags.enableSpeakerSeparation,
      // Video / audio / image redaction default to ENABLED when the flag is
      // absent. These are the long-standing redaction modes existing
      // customers rely on, so if the appConfig fails to load (or predates
      // these keys) we stay permissive rather than silently blocking them —
      // the opposite of `enableDocumentRedaction`, the newer opt-in feature
      // that must be explicitly turned on. CS disables a mode by setting the
      // flag to `false` in the org's feature-flag config; only an explicit
      // false (boolean, or the string "false" the config UI may store)
      // turns it off.
      enableVideoRedaction: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'enableVideoRedaction'),
        featureFlags.enableVideoRedaction
      ),
      enableAudioRedaction: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'enableAudioRedaction'),
        featureFlags.enableAudioRedaction
      ),
      enableImageRedaction: redactionModeEnabled(
        booleanAppConfig(rawConfigs, 'enableImageRedaction'),
        featureFlags.enableImageRedaction
      ),
      bulkEffects: !!appConfigs.bulkEffects || featureFlags.bulkEffects,
      enableDeleteNonEmptyFolder:
        !!appConfigs.enableDeleteNonEmptyFolder ||
        featureFlags.enableDeleteNonEmptyFolder,
      // VE-21114: FORCE-ON OVERRIDE ONLY — do NOT consume this directly.
      // Folders v2 is a property of the platform, and its source of truth is
      // the org metadata (`jsondata.features.v2FoldersEnabled`), which does not
      // flow into applicationConfig and so cannot appear here. This key can
      // only turn the v2 paths on, never off. Read `selectV2FoldersEnabled`,
      // which ORs the two; VE-31036 was two delete paths reading this instead,
      // leaving real v2 orgs on v1 delete logic. Slated for removal once v2 is
      // universal — see the deprecation note on that selector.
      v2FoldersEnabled:
        !!appConfigs.v2FoldersEnabled || featureFlags.v2FoldersEnabled,
      // Opt-in: when an org's `rootFolders` query returns more than one
      // owner-less root, resolve the real one by child-folder count instead of
      // taking the first. Off by default so orgs with a single root keep the
      // existing (cheaper, order-based) resolution.
      rootFolderSmartSelection:
        !!appConfigs.rootFolderSmartSelection ||
        featureFlags.rootFolderSmartSelection,
      relockSleep: Number(featureFlags.relockSleep || 500),
      thumbnailRequestTimeout: Number(
        appConfigs.thumbnailRequestTimeout ??
          featureFlags.thumbnailRequestTimeout ??
          DEFAULT_THUMBNAIL_REQUEST_TIMEOUT
      ), // use ?? because 0 is valid setting
      thumbnailRunningQueueTimeout: Number(
        appConfigs.thumbnailRunningQueueTimeout ??
          featureFlags.thumbnailRunningQueueTimeout ??
          DEFAULT_THUMBNAIL_RUNNING_QUEUE_TIMEOUT
      ), // use ?? because 0 is valid setting
      // TODO add independent field overrides for all feature flags
    };

    // if application config not yet load disable upload
    if (!isApplicationConfigsLoaded) {
      featureFlags = { ...featureFlags, disableUpload: true };
    }

    if (!('redactionCodes' in featureFlags)) {
      featureFlags = { ...featureFlags, redactionCodes: true };
    }

    if (!config?.redactionCodesRegistryId) {
      featureFlags = { ...featureFlags, redactionCodes: false };
    }

    // Custom lexicons (VE-24948 / U5) have NO feature flag here, deliberately.
    // They are a standard settings page, like Redaction Statuses, with the
    // registry id defaulted in code and overridable per environment. The one
    // thing they are gated on is not a flag at all — see the closing paragraph.
    //
    // There were two attempts at gating it, and both were worse than none. The
    // first forced a `customLexicon` flag false whenever
    // `window.config.lexiconRegistryId` was absent — but that id is present or
    // absent for reasons unrelated to whether the feature works: `window.config`
    // comes from the GITIGNORED `static/config.js` when the app runs standalone,
    // and is replaced wholesale by `window.aiwareConfig` under the aiWARE shell
    // (see index.html). So the Lexicons entry silently vanished on a new machine
    // and `git pull` could not fix it. The second kept the flag as a per-org off
    // switch, which nothing asked for and which no sibling settings page has.
    //
    // An environment whose registry genuinely cannot be resolved is reported by
    // `selectIsNotConfigured` — a RUNTIME signal from the schema resolver, not
    // an inference from config shape — and THAT is the off switch. On it the
    // side-nav entry, the apply panel and the upload selector all hide, and the
    // Lexicons page renders an explanatory panel for anyone who deep-links to
    // it. No error is raised: an unprovisioned environment is a legitimate
    // state, not a fault. That is NFR-U5-12 as intended: gate on the state
    // actually observed, not on the shape of a config file.

    return featureFlags;
  }
);

/**
 * True when the org is KNOWN not to have document redaction (VE-29497).
 *
 * `enableDocumentRedaction` is opt-in and, under the aiWARE shell, comes only from the org
 * application config: `window.aiwareConfig` replaces `window.config` and does not carry the
 * flag. That config is fetched once boot finishes, so on a reload or a direct link the flag
 * reads false for every org until it lands. Until then this answers "not known to be off",
 * which makes every gate that uses it fail OPEN on a cold load — and for the rest of the
 * session if the fetch exhausts its retries (Sentry reports that). These are client-side
 * feature gates, not authorization.
 *
 * Shared by the /lexicons and /watermarks route guards and by the document editor's
 * "not enabled" screen. That screen used to read the raw flag and flashed on every reload
 * (VE-31015).
 */
export const selectIsKnownWithoutDocumentRedaction = (
  state: Parameters<typeof selectFeatureFlags>[0]
): boolean =>
  !!selectIsApplicationConfigsLoaded(state) &&
  !selectFeatureFlags(state).enableDocumentRedaction;

// Whether to resolve an org's root folder by child-folder count when several
// owner-less roots come back (see `selectAdminRootFolder`).
export const selectRootFolderSmartSelection = createSelector(
  selectFeatureFlags,
  (featureFlags) => !!featureFlags.rootFolderSmartSelection
);

export const selectTrashRetentionDays = createSelector(
  selectConfig,
  selectApplicationConfigs,
  (config, appConfigs) =>
    Number(
      appConfigs.trashRetentionDays ??
        config.trashRetentionDays ??
        DEFAULT_TRASH_RETENTION_DAYS
    )
);

const selectStore = (s: {
  ['vtn-redact-state-mediaDetails']: MediaDetailsState;
}) => s['vtn-redact-state-mediaDetails'];
export const selectTdoLockInfo = createSelector(
  selectStore,
  (s) => s && s.tdoLockInfo // add check to simplify unit tests
);
export const selectTdoExternallyModified = createSelector(
  selectStore,
  (s) => s && s.isTdoExternallyModified // add check to simplify unit tests
);
export const selectHasInvalidTdoData = createSelector(
  selectStore,
  (s) => s && s.hasInvalidTdoData // add check to simplify unit tests
);
// Status published by a non-mediaDetails editor (e.g. the document editor).
// `null`/undefined for the video flow, which derives status from `tags`.
export const selectExternalRedactionStatus = createSelector(selectStore, (s) =>
  s ? s.externalRedactionStatus : null
);
// VE-31538. Why the request's MERGED DOCUMENT may not be restructured from
// this session, published by the document editor. `null` means it may be —
// we hold the set, or there is no set. Read off the slice locally for the
// same import-cycle reason as the selectors above.
export const selectMergedSetBlock = createSelector(selectStore, (s) =>
  s ? s.mergedSetBlock : null
);
// OLP verdict for the open file (AIWARE_TDO_UPDATE), from checkTdoPermissions.
// `undefined` while the check is in flight, and also when it never ran — a
// non-OLP org leaves it unset, which is what keeps this gate inert there.
// Read off the slice locally, like the selectors above, rather than imported
// from mediaDetails/selectors: user-permissions is imported by that module's
// own consumers, so pulling the other direction risks an import cycle.
export const selectTdoHasPermissions = createSelector(
  selectStore,
  (s) => s && s.tdoHasPermissions
);

const selectUrlMode = (): 'viewOnly' | 'view' | 'review' | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  let mode = params.get('mode');
  // view is alias for viewOnly
  if (mode === 'view') {
    mode = 'viewOnly';
  }
  if (mode === 'viewOnly' || mode === 'review') {
    return mode;
  }
  return null;
};

const selectRolePermissionHelper = createSelector(
  selectTdoLockInfo,
  selectTdoExternallyModified,
  selectHasInvalidTdoData,
  selectTags,
  selectRedactionStatusSettings,
  selectExternalRedactionStatus,
  selectRedactionCustomStatuses,
  selectHasLoadedRedactionStatuses,
  selectTdoHasPermissions,
  selectMergedSetBlock,
  (
    tdoLockInfo,
    isTdoExternallyModified,
    invalidData,
    tags,
    statusSettings,
    externalRedactionStatus,
    customStatuses,
    statusesLoaded,
    tdoHasPermissions,
    mergedSetBlock
  ): RolePermissions => {
    // OLP is checked FIRST, ahead of every other read-only reason. Under
    // object-level permissions a caller without AIWARE_TDO_UPDATE on this file
    // opens it read-only instead of being bounced off the page entirely
    // (VE-20286). It outranks the lock / externally-modified / status-lock
    // branches deliberately: those are transient states the user can wait out or
    // clear, while this one needs an administrator, so its banner is the one
    // worth showing. `undefined` means the check is still in flight — treat that
    // as "no verdict yet" and fall through rather than flashing a read-only
    // editor at a user who does have access.
    if (tdoHasPermissions === false) {
      return { ...viewOnlyPermissions, isOlpViewOnly: true };
    }

    // if tdo is locker by another user force to view only
    if (tdoLockInfo && tdoLockInfo.isLocked) {
      return viewOnlyPermissions;
    }
    // VE-31538: or by another user holding the MERGED DOCUMENT this file is
    // part of. Same band as the file's own lock — both are "somebody else is
    // editing, wait" — but a distinct state, because the file itself is free
    // and the banner has to explain why it is still read-only.
    //
    // ONLY A FOREIGN LOCK, AND ONLY OVER A MEMBER. A holder is editing the
    // SET, so a file in it may not be edited behind them — but a file that
    // is merely in the same request is no part of what they hold, and
    // freezing it was a bug the moment an outsider started reading the lock
    // at all. `readOnly` is already whichever band above produced it.
    if (mergedSetBlock?.reason === 'foreign' && mergedSetBlock.openFileInSet) {
      return viewOnlyPermissions;
    }
    // if tdo is externally modified also force to view only
    if (isTdoExternallyModified) {
      return viewOnlyPermissions;
    }

    // if invalid data force to view only
    if (invalidData) {
      return viewOnlyPermissions;
    }

    // Check URL mode parameters ( only allow forcing a more restrictive mode, never a less restrictive mode - ex: editor should never be able to access review or viewOnly modes, but viewOnly can access review or editor modes)
    // TODO: We can update when implement on Folder View
    const urlMode = selectUrlMode();
    if (urlMode === 'viewOnly') {
      return viewOnlyPermissions;
    }

    // check for redaction status edit lock. Editors that don't populate this
    // slice's DetailTDO `tags` (the document editor) publish their status via
    // `externalRedactionStatus`; prefer it when present, else derive from tags
    // (the video flow). Either way it runs through the same lock-edits check.
    const inRedactionTag = tags?.find(
      (tag) => tag?.value === IN_REDACTION_TAG_KEY && !!tag?.redactionStatus
    );
    const statusValue =
      externalRedactionStatus ??
      (inRedactionTag ? getRedactionStatusIdentity(inRedactionTag) : undefined);
    const statusKey = resolveRedactionStatusKey(statusValue);
    const redactionStatusLockEdits = isRedactionStatusLockEdits(
      statusValue,
      statusSettings,
      customStatuses,
      // Fail closed: until the org's statuses load, a custom-status UUID is
      // indistinguishable from an orphan and would hand out full edit rights
      // on a file the org locked (VE-24546).
      statusesLoaded
    );
    if (redactionStatusLockEdits) {
      return {
        ...statusLockChangePermissions,
        ...(statusKey === REDACTION_STATUS_CORRECTION_NEEDED && {
          sendRedactedFile: false,
        }),
      };
    }

    // check remaining urlMode
    if (urlMode === 'review') {
      return reviewModePermissions;
    }

    // prevent sending file when correction needed
    if (statusKey === REDACTION_STATUS_CORRECTION_NEEDED) {
      return {
        ...editorPermissions,
        sendRedactedFile: false,
      };
    }

    // if made it this far must be an editor
    return editorPermissions;
  }
);

/**
 * May this session hold a TDO lock, IGNORING the merged-document verdict?
 *
 * `lockTdo` cannot answer this, and that is the whole reason this exists:
 * a foreign set lock resolves to `viewOnlyPermissions`, so it clears
 * `lockTdo` — and `lockTdo` is what `suspendedLocksSaga` consults to decide
 * whether to take the set. The service that publishes the verdict was
 * therefore disabled by its own output: the session stopped refreshing a
 * record it still held, and could not claim the set for a further heartbeat
 * once the holder left (VE-31538).
 *
 * The first fix for that widened `lockTdo` itself, which was wrong — it also
 * gates the PER-FILE lock heartbeat (`mediaDetails/saga/view.ts`), so a
 * read-only bystander began holding the file lock on whatever member they
 * were looking at, and could then take the set from the reviewer actually
 * editing it. The flag has to keep one meaning; the saga gets its own
 * question instead.
 *
 * This restates `selectRolePermissionHelper`'s cascade MINUS the set-lock
 * band: every other reason that resolves to view-only, in the same order.
 *
 * THE STATUS LOCK IS DELIBERATELY ABSENT, and that is load-bearing rather
 * than inherited. A member sitting at Pending Review or Complete resolves to
 * `statusLockChangePermissions`, which an editor working through a set
 * passes over all the time — and if the set lock lapsed there, the pass
 * through one finished file would be a window for somebody else to take the
 * merged document out from under them. Status says what may be done to a
 * FILE; it says nothing about who is working on the set.
 *
 * URL REVIEW MODE IS THE OPPOSITE CASE and is excluded. It is not a state a
 * session passes through: the editor carries the query across document
 * switches (`editorQuery`), so a tab opened with `?mode=review` is review
 * for its whole life and can never change the set — `editMergedDocument` is
 * false for it by construction. Holding the set there blocks seven
 * operations for everybody else while the holder can perform none of them,
 * and a review link is for looking at somebody's work, not for stopping it.
 * The per-file lock is unaffected: review still takes that, on purpose
 * (`reviewModePermissions.lockTdo`), because what review DOES — status,
 * regenerating the output, sending the file — is all per file.
 */
export const selectMayHoldTdoLock = createSelector(
  selectTdoHasPermissions,
  selectTdoLockInfo,
  selectTdoExternallyModified,
  selectHasInvalidTdoData,
  // AN INPUT, not a call inside the body. The other four are `undefined`,
  // `null`, `false`, `false` on a healthy session, so they are reference-
  // equal from one call to the next and `createSelector` answers from its
  // cache — a mode read inside the body would be consulted once, on whatever
  // URL happened to be current then, and never again. It returns a string or
  // null, so the memo still holds.
  () => selectUrlMode(),
  (
    tdoHasPermissions,
    tdoLockInfo,
    isTdoExternallyModified,
    invalidData,
    urlMode
  ): boolean =>
    tdoHasPermissions !== false &&
    !tdoLockInfo?.isLocked &&
    !isTdoExternallyModified &&
    !invalidData &&
    urlMode !== 'viewOnly' &&
    urlMode !== 'review'
);

export const selectRolePermissions = createSelector(
  selectRolePermissionHelper,
  selectContentTypeHasImage,
  selectMergedSetBlock,
  selectTdoHasPermissions,
  (
    rolePermissions,
    hasImage,
    mergedSetBlock,
    tdoHasPermissions
  ): RolePermissions => {
    /**
     * `editMergedDocument` IS DERIVED ONCE, HERE (VE-31538).
     *
     * It is a function of three facts and of none of the others: who holds
     * the SET, the reviewer's mode, and their object-level permission. The
     * first attempt spread an override into four of the eight bands above,
     * which made the answer depend on BRANCH ORDER — an order chosen for
     * which banner to show, not for this. The file-lock band sits above the
     * set-lock band, and opening a member takes both locks, so the common
     * case took the wrong branch and handed set-wide rights to someone
     * looking at a set another reviewer held. A later band could forget it
     * just as silently.
     *
     * Derived after the band, nothing can forget it. What it must NOT read
     * is the open file's own state — a status lock freezes that file's
     * redactions, not the release's composition, which is the position
     * `canTriggerRedactFile` already takes.
     *
     * IT ASKS WHETHER WE HOLD THE SET, not whether anyone else does, and the
     * difference is the whole point of `MergedSetBlock` carrying a reason.
     * Reading `isLocked` alone meant any session with no foreign lock in
     * sight got set-wide rights: open a file in the request that is NOT a
     * member of the merged document and nothing takes the set lock, so the
     * panel offered Update, Add, reorder and Delete to a session holding
     * nothing — two of which could then rebuild the same release at once.
     */
    const urlMode = selectUrlMode();
    const resolved: RolePermissions = {
      ...rolePermissions,
      editMergedDocument:
        !mergedSetBlock &&
        tdoHasPermissions !== false &&
        urlMode !== 'viewOnly' &&
        urlMode !== 'review',
    };
    // If content type is image, disable certain features
    if (hasImage) {
      return {
        ...resolved,
        showAudioTab: false,
        showStickToVideo: false,
        showSetTimeStamp: false,
        showTrackOption: false,
        showTrimOptions: false,
        editGlobalSettings: false,
        transcribeAudio: false,
        generateVoiceMask: false,
      };
    }

    return resolved;
  }
);
